/**
 * useMediaUrls — pide al servidor URLs firmadas para el media visible del chat.
 *
 * Paso 5 del Bloque E. El bucket está cerrado al cliente (`storage.rules`), así que la
 * única forma de ver una imagen es que `getInboxMediaUrls` la firme. Firma 15 min.
 *
 * ⚠️ POR QUÉ EL OBSERVER SE CREA UNA SOLA VEZ: `mensajesParaRender` en ChatDetalle es un
 * `useMemo` que cambia de identidad en CADA snapshot de Firestore. Un efecto que lo
 * listara como dependencia recrearía el IntersectionObserver constantemente y las
 * suscripciones se perderían a medio vuelo. Aquí el observer vive en un ref y se crea con
 * `[]`; los nodos se registran solos vía ref callback. El observer no depende de la lista.
 *
 * ⚠️ LOS IDS `temp_` NO SE PIDEN NUNCA. `mensajesParaRender` incluye los mensajes
 * optimistas de `pendingMensajes`, con ids `temp_…` que NO existen en la subcolección
 * `mensajes`. El callable trata un id inexistente como intruso y **rechaza el lote
 * COMPLETO** con permission-denied, así que un solo temp_ dejaría sin imágenes a toda la
 * pantalla. El filtro de esta capa es la ÚNICA defensa: el servidor no puede distinguir
 * un optimista de un sondeo, ni debe.
 *
 * ⚠️ CADUCIDAD PROACTIVA: si `expiresAt` ya pasó se pide URL nueva ANTES de intentar
 * cargar. Esperar el `onError` gastaría el único reintento en una caducidad esperada, que
 * no es un fallo. El reintento queda para fallos de verdad.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

/** Igual que MAX_IDS del callable. NO es FIRMAS_EN_PARALELO (=10), que es interno suyo. */
export const LOTE = 50;

/** Ventana para juntar lo que entra en pantalla antes de pedir. */
export const DEBOUNCE_MS = 200;

/** Un solo reintento por msgId, y sólo para fallos reales. */
export const MAX_REINTENTOS = 1;

/** Margen antes de `expiresAt` para considerar la URL agotada. */
const MARGEN_CADUCIDAD_MS = 30 * 1000;

export const esTemporal = (id) => typeof id === 'string' && id.startsWith('temp_');

const trozos = (arr, n) => Array.from(
  { length: Math.ceil(arr.length / n) },
  (_, i) => arr.slice(i * n, i * n + n),
);

/**
 * @returns {{
 *   estado: (msgId:string) => 'pendiente'|'ok'|'sin-media'|'error',
 *   url: (msgId:string) => string|null,
 *   texto: (msgId:string) => string|null,
 *   tamano: (msgId:string) => number|null,
 *   textoNoVerificado: (msgId:string) => string|null,
 *   observar: (msgId:string) => (node:Element|null) => void,
 *   alFallar: (msgId:string) => void,
 *   alCargar: (msgId:string) => void,
 * }}
 */
export default function useMediaUrls({ organizationId, chatId }) {
  // { [msgId]: { url, expiresAt } | null }  — null = el servidor NO tiene media para él
  const [mapa, setMapa] = useState({});
  const [errores, setErrores] = useState({});
  // ⚠️ MAPA APARTE, Y NO UN CAMPO DE `mapa`. La entrada de `mapa` se pone a `null`
  // cuando el servidor no tiene binario, y `alFallar` la BORRA para reintentar: las dos
  // cosas se llevarían la transcripción por delante. El binario se retiene 30 días y el
  // texto no caduca, así que un audio viejo debe seguir leyéndose aunque ya no suene —
  // que es justo cuando la transcripción es lo único que queda.
  const [textos, setTextos] = useState({});
  // Bytes de los DOCUMENTOS (B1.2, 20-oct-2026), aparte por el mismo motivo que `textos`:
  // `alFallar` borra la entrada de `mapa` y se llevaría el tamaño por delante.
  const [tamanos, setTamanos] = useState({});
  // La transcripción que el filtro de Whisper RECHAZÓ (20-oct-2026): sólo para el operador,
  // marcada «no verificada». Aparte por el mismo motivo que `textos`.
  const [noVerificados, setNoVerificados] = useState({});

  // Refs y no estado: se leen y escriben dentro de callbacks que no deben provocar
  // re-render ni re-crear el observer.
  const pendientes = useRef(new Set());   // msgIds vistos, aún sin pedir
  const pedidos = useRef(new Set());      // ya pedidos (evita repetir en cada scroll)
  const reintentos = useRef(new Map());   // msgId -> nº de reintentos gastados
  const temporizador = useRef(null);
  const observer = useRef(null);
  const nodos = useRef(new Map());        // msgId -> nodo observado
  const vivo = useRef(true);

  // Cambiar de chat invalida todo: las URLs son de otro chat.
  useEffect(() => {
    setMapa({});
    setErrores({});
    setTextos({});
    setTamanos({});
    setNoVerificados({});
    pendientes.current.clear();
    pedidos.current.clear();
    reintentos.current.clear();
  }, [chatId, organizationId]);

  const pedir = useCallback(async (ids) => {
    if (!ids.length || !chatId) return;
    const call = httpsCallable(functions, 'getInboxMediaUrls');
    for (const lote of trozos(ids, LOTE)) {
      try {
        const r = await call({ organizationId: organizationId || null, chatId, msgIds: lote });
        const urls = r.data?.urls || {};
        const recibidos = r.data?.textos || {};
        const bytes = r.data?.tamanos || {};
        const dudosos = r.data?.noVerificados || {};
        const expiresAt = r.data?.expiresAt || 0;
        if (!vivo.current) return;
        // Se ACUMULA y no se reemplaza por lote: sin texto, un id simplemente no
        // aparece, y sobreescribir borraría lo que ya se sabía de otros lotes.
        if (Object.keys(recibidos).length) setTextos((prev) => ({ ...prev, ...recibidos }));
        if (Object.keys(bytes).length) setTamanos((prev) => ({ ...prev, ...bytes }));
        if (Object.keys(dudosos).length) setNoVerificados((prev) => ({ ...prev, ...dudosos }));
        setMapa((prev) => {
          const sig = { ...prev };
          // Los que el servidor OMITE no tienen media: se marcan con null, que es
          // distinto de "aún no preguntamos" (ausente). Sin esa distinción la UI
          // reintentaría para siempre algo que nunca va a existir.
          lote.forEach((id) => { sig[id] = urls[id] ? { url: urls[id], expiresAt } : null; });
          return sig;
        });
      } catch (e) {
        if (!vivo.current) return;
        // Fallo de la petición (red, permisos, tope). NO es "sin media".
        setErrores((prev) => {
          const sig = { ...prev };
          lote.forEach((id) => { sig[id] = e?.message || 'error'; });
          return sig;
        });
      }
    }
  }, [chatId, organizationId]);

  const vaciar = useCallback(() => {
    const ids = [...pendientes.current];
    pendientes.current.clear();
    if (ids.length) pedir(ids);
  }, [pedir]);

  const encolar = useCallback((msgId) => {
    if (esTemporal(msgId)) return;          // ver la nota de arriba: rompería el lote
    if (pedidos.current.has(msgId)) return;
    pedidos.current.add(msgId);
    pendientes.current.add(msgId);
    clearTimeout(temporizador.current);
    temporizador.current = setTimeout(vaciar, DEBOUNCE_MS);
  }, [vaciar]);

  // UNA sola instancia, con `[]`: no se recrea aunque la lista de mensajes cambie.
  useEffect(() => {
    vivo.current = true;
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const id = e.target.getAttribute('data-msg-id');
        if (id) encolar(id);
      });
    }, { rootMargin: '200px' });   // adelanta la petición antes de entrar en pantalla

    return () => {
      vivo.current = false;
      clearTimeout(temporizador.current);
      observer.current?.disconnect();
      observer.current = null;
    };
  }, [encolar]);

  /** ref callback por mensaje: registra y da de baja el nodo. */
  const observar = useCallback((msgId) => (node) => {
    const anterior = nodos.current.get(msgId);
    if (anterior && anterior !== node) {
      observer.current?.unobserve(anterior);
      nodos.current.delete(msgId);
    }
    if (node) {
      node.setAttribute('data-msg-id', msgId);
      nodos.current.set(msgId, node);
      observer.current?.observe(node);
    }
  }, []);

  const caducada = (entrada) => !!entrada
    && entrada.expiresAt > 0
    && Date.now() > entrada.expiresAt - MARGEN_CADUCIDAD_MS;

  const estado = useCallback((msgId) => {
    if (errores[msgId]) return 'error';
    const e = mapa[msgId];
    if (e === undefined) return 'pendiente';   // no preguntado o en vuelo
    if (e === null) return 'sin-media';        // el servidor no tiene binario: PERMANENTE
    if (caducada(e)) return 'pendiente';       // se re-pide sola, no es error
    return 'ok';
  }, [mapa, errores]);

  const url = useCallback((msgId) => {
    const e = mapa[msgId];
    return e && !caducada(e) ? e.url : null;
  }, [mapa]);

  /**
   * La transcripción del audio o la descripción de la imagen, o null.
   *
   * INDEPENDIENTE del estado de la URL a propósito: `sin-media`, `error` y `pendiente`
   * pueden tener texto igualmente. Ver la nota de `textos` arriba.
   */
  const texto = useCallback((msgId) => textos[msgId] || null, [textos]);

  /** Tamaño en bytes de un documento, o null si el servidor no lo dio. */
  const tamano = useCallback((msgId) => tamanos[msgId] || null, [tamanos]);

  /** La transcripción NO verificada de una nota de voz, o null. */
  const textoNoVerificado = useCallback((msgId) => noVerificados[msgId] || null, [noVerificados]);

  // Caducidad proactiva: al pasar `expiresAt` se vuelve a pedir sin esperar el fallo.
  useEffect(() => {
    const vencidas = Object.entries(mapa)
      .filter(([, e]) => caducada(e))
      .map(([id]) => id);
    if (!vencidas.length) return;
    vencidas.forEach((id) => pedidos.current.delete(id));
    vencidas.forEach(encolar);
  }, [mapa, encolar]);

  /** `onError` del <img>/<audio>: un reintento, y sólo para fallos reales. */
  const alFallar = useCallback((msgId) => {
    const usados = reintentos.current.get(msgId) || 0;
    if (usados >= MAX_REINTENTOS) {
      setErrores((prev) => ({ ...prev, [msgId]: 'no-carga' }));
      return;
    }
    reintentos.current.set(msgId, usados + 1);
    pedidos.current.delete(msgId);
    setMapa((prev) => { const s = { ...prev }; delete s[msgId]; return s; });
    encolar(msgId);
  }, [encolar]);

  /**
   * El pipeline avisó de que el media está listo (`mediaListo: true` en el doc del
   * mensaje, que llega por onSnapshot). Se vuelve a pedir SÓLO ese msgId.
   *
   * ⚠️ NO invalida el mapa entero: los demás mensajes no cambiaron y re-pedirlos
   * costaría round-trips y firmas por nada.
   *
   * ⚠️ SIN BUCLE, por dos frenos independientes:
   *   · si ya está en `ok`, no hace nada — un `mediaListo` repetido no re-pide
   *   · lo llama un efecto atado a la TRANSICIÓN de `mediaListo`, que ocurre una vez.
   *     Si la respuesta sigue sin traer URL, el estado vuelve a `sin-media` y nada
   *     vuelve a disparar: no hay reintento en cadena.
   */
  const refrescar = useCallback((msgId) => {
    if (esTemporal(msgId)) return;
    const e = mapa[msgId];
    if (e && !caducada(e)) return;        // ya tiene URL válida: nada que hacer
    pedidos.current.delete(msgId);
    encolar(msgId);
  }, [mapa, encolar]);

  /**
   * `onLoad`: resetea el contador. Se hace AQUÍ y no al recibir la URL — una URL que
   * nunca llega a cargar no debe devolver reintentos, o el bucle se reabre.
   */
  const alCargar = useCallback((msgId) => {
    reintentos.current.delete(msgId);
  }, []);

  return { estado, url, texto, tamano, textoNoVerificado, observar, alFallar, alCargar, refrescar };
}
